import { useEffect, useState } from "react";
import { Pencil, Check, X, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";

const LEVEL_LABEL = { 1: "LEVEL 1 · LEARN · +10 PTS", 2: "LEVEL 2 · UNDERSTAND · +25 PTS", 3: "LEVEL 3 · TASTE & EXPERT · +50 PTS" };
const EMPTY_FORM = { level: 2, slug: "", q: "", options: ["", "", "", ""], answer: 0 };

export const AdminQuizzes = () => {
  const [questions, setQuestions] = useState([]);
  const [open, setOpen] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = () => api.get("/admin/quiz-questions").then((r) => setQuestions(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/dishes").then((r) => setDishes(r.data)).catch(() => {});
  }, []);

  const startEdit = (q) => {
    setOpen(q.qid);
    setDraft({ q: q.q, options: [...q.options], answer: q.answer });
  };
  const save = async () => {
    try {
      await api.put(`/admin/quiz-questions/${open}`, draft);
      toast.success("Question saved");
      setOpen(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };
  const toggleStatus = async (q) => {
    try {
      await api.put(`/admin/quiz-questions/${q.qid}`, { status: q.status === "approved" ? "disabled" : "approved" });
      load();
    } catch {
      toast.error("Could not change status — try again");
    }
  };

  const addQuestion = async () => {
    try {
      await api.post("/admin/quiz-questions", { ...form, slug: form.slug || null });
      toast.success("Question added — live for guests now");
      setShowAdd(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add the question");
    }
  };

  const removeQuestion = async (qid) => {
    try {
      await api.delete(`/admin/quiz-questions/${qid}`);
      toast.success("Question deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const formValid = form.q.trim() && form.options.every((o) => o.trim()) && (form.level === 3 || form.slug);

  return (
    <div data-testid="admin-quizzes" className="space-y-4">
      <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
        <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">QUIZ QUESTION APPROVAL</p>
        <p className="text-xs text-white/50">Every question guests can see, in one place. Edit wording, options or the correct answer — or DISABLE a question to pull it from rotation instantly. Points are fixed per level.</p>
      </div>
      <div className="rounded-xl bg-[#121212] border border-white/10 p-4" data-testid="quiz-composer">
        <button data-testid="quiz-composer-toggle" onClick={() => setShowAdd(!showAdd)}
          className="w-full flex items-center justify-between">
          <span className="text-[10px] tracking-widest text-[#D4AF37] font-bold">WRITE A NEW QUESTION</span>
          <Plus size={15} className={`text-[#D4AF37] transition-transform ${showAdd ? "rotate-45" : ""}`} />
        </button>
        {showAdd && (
          <div className="mt-3 space-y-2.5">
            <div className="flex gap-2">
              {[1, 2, 3].map((lv) => (
                <button key={lv} data-testid={`composer-level-${lv}`} onClick={() => setForm({ ...form, level: lv })}
                  className={`flex-1 rounded-full py-2 text-[10px] font-bold tracking-widest border ${form.level === lv ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-[#1A1A1A] text-white/60 border-white/10"}`}>
                  {`L${lv} · +${[10, 25, 50][lv - 1]}`}
                </button>
              ))}
            </div>
            <select
              data-testid="composer-dish-select"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              className="w-full bg-[#1A1A1A] border border-white/15 rounded-full px-4 py-2.5 text-xs focus:outline-none focus:border-[#D4AF37]/60"
            >
              <option value="">{form.level === 3 ? "Region question (no dish)" : "Choose the dish…"}</option>
              {dishes.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}
            </select>
            <textarea
              data-testid="composer-q-input"
              value={form.q}
              onChange={(e) => setForm({ ...form, q: e.target.value })}
              rows={2}
              placeholder="The question guests will see…"
              className="w-full bg-[#1A1A1A] border border-white/15 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-[#D4AF37]/60"
            />
            {form.options.map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <button
                  data-testid={`composer-correct-${i}`}
                  onClick={() => setForm({ ...form, answer: i })}
                  aria-label={`Mark option ${i + 1} correct`}
                  className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center ${form.answer === i ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "border-white/25 text-transparent"}`}
                >
                  <Check size={13} />
                </button>
                <input
                  data-testid={`composer-option-${i}`}
                  value={opt}
                  onChange={(e) => {
                    const options = [...form.options];
                    options[i] = e.target.value;
                    setForm({ ...form, options });
                  }}
                  placeholder={`Option ${i + 1}`}
                  className="flex-1 bg-[#1A1A1A] border border-white/15 rounded-full px-3 py-2 text-xs focus:outline-none focus:border-[#D4AF37]/60"
                />
              </div>
            ))}
            <p className="text-[9px] text-white/35">Gold circle = correct answer. Keep facts inside the approved story texts.</p>
            <button data-testid="composer-add-btn" onClick={addQuestion} disabled={!formValid}
              className="w-full bg-[#D4AF37] disabled:bg-[#1A1A1A] disabled:text-white/40 text-black text-[10px] font-bold rounded-full py-2.5 tracking-widest">
              ADD QUESTION
            </button>
          </div>
        )}
      </div>
      {[1, 2, 3].map((lv) => {
        const rows = questions.filter((q) => q.level === lv);
        return (
          <div key={lv}>
            <p className="text-[10px] tracking-[0.25em] text-[#D4AF37] font-bold mb-2">{LEVEL_LABEL[lv]} · {rows.length}</p>
            <div className="rounded-xl bg-[#121212] border border-white/10 divide-y divide-white/5">
              {rows.map((q) => (
                <div key={q.qid} data-testid={`quizq-${q.qid}`} className="px-4 py-3">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[9px] tracking-widest text-white/35">{q.slug || "REGION"}{q.custom ? " · CUSTOM" : ""}{q.edited ? " · EDITED" : ""}</p>
                      <p className="text-xs font-semibold mt-0.5">{q.q}</p>
                    </div>
                    <button
                      data-testid={`quizq-status-${q.qid}`}
                      onClick={() => toggleStatus(q)}
                      className={`shrink-0 text-[9px] font-bold tracking-widest rounded-full px-3 py-1.5 border ${
                        q.status === "approved" ? "border-[#D4AF37]/60 text-[#D4AF37]" : "border-[#E25822]/60 text-[#E25822]"
                      }`}
                    >
                      {q.status === "approved" ? "APPROVED" : "DISABLED"}
                    </button>
                    {q.qid.startsWith("custom:") && (
                      <button data-testid={`quizq-delete-${q.qid}`} onClick={() => removeQuestion(q.qid)}
                        aria-label="Delete question" className="shrink-0 text-[#E25822] p-1.5">
                        <Trash2 size={14} />
                      </button>
                    )}
                    <button data-testid={`quizq-edit-${q.qid}`} onClick={() => (open === q.qid ? setOpen(null) : startEdit(q))}
                      aria-label="Edit question" className="shrink-0 text-white/50 p-1.5">
                      {open === q.qid ? <X size={14} /> : <Pencil size={14} />}
                    </button>
                  </div>
                  {open === q.qid && draft && (
                    <div className="mt-3 space-y-2" data-testid={`quizq-editor-${q.qid}`}>
                      <textarea
                        data-testid="quizq-text-input"
                        value={draft.q}
                        onChange={(e) => setDraft({ ...draft, q: e.target.value })}
                        rows={2}
                        className="w-full bg-[#1A1A1A] border border-white/15 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-[#D4AF37]/60"
                      />
                      {draft.options.map((opt, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <button
                            data-testid={`quizq-correct-${i}`}
                            onClick={() => setDraft({ ...draft, answer: i })}
                            aria-label={`Mark option ${i + 1} correct`}
                            className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center ${
                              draft.answer === i ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "border-white/25 text-transparent"
                            }`}
                          >
                            <Check size={13} />
                          </button>
                          <input
                            data-testid={`quizq-option-input-${i}`}
                            value={opt}
                            onChange={(e) => {
                              const options = [...draft.options];
                              options[i] = e.target.value;
                              setDraft({ ...draft, options });
                            }}
                            className="flex-1 bg-[#1A1A1A] border border-white/15 rounded-full px-3 py-2 text-xs focus:outline-none focus:border-[#D4AF37]/60"
                          />
                        </div>
                      ))}
                      <p className="text-[9px] text-white/35">Gold circle = correct answer. Keep facts inside the approved story texts.</p>
                      <button data-testid="quizq-save" onClick={save}
                        className="w-full bg-[#D4AF37] text-black text-[10px] font-bold rounded-full py-2.5 tracking-widest">
                        SAVE QUESTION
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AdminQuizzes;
